/**
 * HFT Studio v2 — data availability calendar with drill-down into
 * candles, ticks, and options chains across all TrueData pipeline layers.
 */
import React from 'react';
import { Box, Tab, Tabs } from '@mui/material';
import { Calendar, Link2, BarChart3, Activity } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
import { useHftStore } from './store';
import BreadcrumbNav from './components/BreadcrumbNav';
import CalendarHeatMap from './components/CalendarHeatMap';
import DayOverview from './components/DayOverview';
import OptionsChainView from './components/OptionsChainView';
import SegmentExplorer from './components/SegmentExplorer';
import TelemetryPanel from './components/TelemetryPanel';
import './hft-studio.css';

const slideVariants = {
  enter: { opacity: 0, x: 40 },
  center: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: -40 },
};

type TopTab = 'explorer' | 'chain';

export default function HftStudioPage() {
  const { current, push, reset } = useHftStore();
  const view = current.view;
  const [topTab, setTopTab] = React.useState<TopTab>('explorer');

  const handleTabChange = (_: unknown, newVal: TopTab) => {
    setTopTab(newVal);
    if (newVal === 'chain') {
      push('chain', { underlying: 'NIFTY', date: current.nav.date || new Date().toISOString().slice(0, 10) });
    } else {
      reset();
    }
  };

  return (
    <Box className="hft-studio" sx={{ px: 1, py: 1, height: '100%', overflow: 'auto' }}>
      <TelemetryPanel />

      {/* Top-level tabs: Data Explorer vs Options Chain */}
      <Tabs
        value={topTab}
        onChange={handleTabChange}
        sx={{ mb: 1.5, minHeight: 40, '& .MuiTab-root': { minHeight: 40, py: 0.5, textTransform: 'none', fontWeight: 600 } }}
      >
        <Tab icon={<Calendar size={16} />} iconPosition="start" label="Data Explorer" value="explorer" />
        <Tab icon={<Link2 size={16} />} iconPosition="start" label="Options Chain" value="chain" />
      </Tabs>

      {topTab === 'explorer' && (
        <>
          <BreadcrumbNav />
          <AnimatePresence mode="wait">
            <motion.div
              key={view + (current.nav.date || '') + (current.nav.segment || '')}
              variants={slideVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ duration: 0.18, ease: 'easeOut' }}
            >
              {view === 'calendar' && <CalendarHeatMap />}
              {view === 'day' && <DayOverview />}
              {view === 'segment' && <SegmentExplorer />}
              {view === 'chain' && <OptionsChainView />}
            </motion.div>
          </AnimatePresence>
        </>
      )}

      {topTab === 'chain' && <OptionsChainView />}
    </Box>
  );
}
